import { RootState } from '@/modules/ewwms/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
import { State } from './state';

import { useEwwmsCommonStore } from '../../ewwmsCommonStore';
import { ActionTypes as BestandActionTypes } from '../Bestand/actions';

import ApiService from '@/modules/ewwms/services/ApiService';
import {
  Bestellung,
  GetBestellungRequest,
  SetListeartikelKommissionRequest,
  UpdateBestellungLkwKennzeichenRequest,
  GetBestellungDokumentRequest,
} from '@/modules/ewwms/models/Lager/BestellungenModels';
import { Listeartikel } from '@/modules/ewwms/models/Lager/ListeartikelModels';
import { ArchivComponenteFile } from '@/modules/ewwms/models/ArchivModels';

export enum ActionTypes {
  GetBestellungen = 'GETBESTELLUNGEN',
  GetUpdateBestellungen = 'GETUPDATEBESTELLUNGEN',
  SetBestellungLkwKennzeichen = 'SETBESTELLUNGLKWKENNZEICHEN',
  SetBestellungAbgeschlossen = 'SETBESTELLUNGABGESCHLOSSEN',
  GetBestellungPaletten = 'GETBESTELLUNGPALETTEN',
  SetListeartikelKommission = 'SETLISTEARTIKELKOMMISSION',
  SetInitDate = 'SETINITDATE',
  ResetBestellungen = 'RESETBESTELLUNGEN',

  GetBestellungDokument = 'GETBESTELLUNGDOKUMENT',
  UpdateBestellungDokumente = 'UPDATEBESTELLUNGDOKUMENTE',

  AddOrUpdatePaletten = 'ADDORUPDATEPALETTEN',
  AddOrUpdatePalette = 'ADDORUPDATEPALETTE',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetBestellungen](context: ActionArguments, data: GetBestellungRequest): Promise<void>;
  [ActionTypes.GetUpdateBestellungen](context: ActionArguments, data: GetBestellungRequest): Promise<void>;
  [ActionTypes.SetBestellungLkwKennzeichen](context: ActionArguments, data: UpdateBestellungLkwKennzeichenRequest): Promise<void>;
  [ActionTypes.SetBestellungAbgeschlossen](context: ActionArguments, data: number): Promise<void>;
  [ActionTypes.GetBestellungPaletten](context: ActionArguments, data: number): Promise<void>;
  [ActionTypes.SetListeartikelKommission](context: ActionArguments, data: SetListeartikelKommissionRequest): Promise<void>;
  [ActionTypes.SetInitDate](context: ActionArguments): void;
  [ActionTypes.ResetBestellungen](context: ActionArguments): void;
  [ActionTypes.AddOrUpdatePaletten](context: ActionArguments, data: Array<Listeartikel>): void;
  [ActionTypes.AddOrUpdatePalette](context: ActionArguments, data: Listeartikel): void;

  [ActionTypes.GetBestellungDokument](context: ActionArguments, data: GetBestellungDokumentRequest): Promise<void>;
  [ActionTypes.UpdateBestellungDokumente](context: ActionArguments, data: number): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.GetBestellungen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.bestellungenErrorMsg = '';
    status.bestellungenLoading = true;
    commonStore.setStatus(status);
    commit(MutationTypes.SetBestellungen, new Array<Bestellung>());

    return ApiService.getBestellungen(data)
      .then((res) => {
        const bestellungen: Array<Bestellung> = res.data;
        commit(MutationTypes.SetBestellungen, bestellungen);
      })
      .catch((error) => {
        console.log(error);
        status.bestellungenErrorMsg = error.toString();
      })
      .finally(() => {
        status.bestellungenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetUpdateBestellungen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.bestellungenErrorMsg = '';
    commonStore.setStatus(status);

    return ApiService.getBestellungen(data)
      .then((res) => {
        const bestellungen: Array<Bestellung> = res.data;
        commit(MutationTypes.SetBestellungen, bestellungen);
      })
      .catch((error) => {
        console.log(error);
        status.bestellungenErrorMsg = error.toString();
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetBestellungDokument]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.bestellungenErrorMsg = '';
    status.getBestellungDocLoading = true;
    commit(MutationTypes.SetBestellungDoc, null);
    commonStore.setStatus(status);

    return ApiService.getBestellungDokument(data)
      .then(async (res) => {
        const base64String = await blobToBase64(res.data);
        var bestellungDoc = new ArchivComponenteFile(res.data.size, base64String);
        // const bestellungDoc: ArchivComponenteFile = res.data
        commit(MutationTypes.SetBestellungDoc, bestellungDoc);
      })
      .catch((error) => {
        if (error.response.status == 404) {
          console.log(error.response);
          // status.bestellungenErrorMsg = error.response.data.detail
          status.bestellungenErrorMsg = 'Es wurde kein Dokument gefunden.';
        } else {
          status.bestellungenErrorMsg = error.toString();
        }
      })
      .finally(() => {
        status.getBestellungDocLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UpdateBestellungDokumente]({}, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.bestellungenErrorMsg = '';
    status.getBestellungDocLoading = true;
    commonStore.setStatus(status);

    return ApiService.updateBestellungDokumente(data)
      .then((res) => {
        if (res.data == false) {
          status.bestellungenErrorMsg = 'Es ist ein Fehler aufgetreten.';
        }
      })
      .catch((error) => {
        if (error.response.status == 404) {
          console.log(error.response);
          // status.bestellungenErrorMsg = error.response.data.detail
          status.bestellungenErrorMsg = 'Es wurde kein Dokument gefunden.';
        } else {
          status.bestellungenErrorMsg = error.toString();
        }
      })
      .finally(() => {
        status.getBestellungDocLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetBestellungLkwKennzeichen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.updateBestellungenErrorMsg = '';
    status.updateBestellungenLoading = true;
    commonStore.setStatus(status);

    return ApiService.updateLkwKennzeichen(data)
      .then((res) => {
        const bestellung: Bestellung = res.data;
        commit(MutationTypes.SetUpdatedBestellung, bestellung);
      })
      .catch((error) => {
        console.log(error);
        status.updateBestellungenErrorMsg = error.toString();
      })
      .finally(() => {
        status.updateBestellungenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetBestellungAbgeschlossen]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.updateBestellungenErrorMsg = '';
    status.updateBestellungenLoading = true;
    commonStore.setStatus(status);

    return ApiService.setBestellungAbgeschlossen(data)
      .then((res) => {
        const bestellung: Bestellung = res.data;
        commit(MutationTypes.SetUpdatedBestellung, bestellung);
      })
      .catch((error) => {
        console.log(error);
        status.updateBestellungenErrorMsg = error.toString();
      })
      .finally(() => {
        status.updateBestellungenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.GetBestellungPaletten]({ commit }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.bestellungPalettenErrorMsg = '';
    status.getBestellungPalettenLoading = true;
    commonStore.setStatus(status);

    return ApiService.getBestellungPaletten(data)
      .then((res) => {
        const paletten: Array<Listeartikel> = res.data;
        commit(MutationTypes.AddOrUpdatePaletten, paletten);
      })
      .catch((error) => {
        console.log(error);
        status.bestellungPalettenErrorMsg = error.toString();
      })
      .finally(() => {
        status.getBestellungPalettenLoading = false;
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SetListeartikelKommission]({ commit, dispatch }, data) {
    const commonStore = useEwwmsCommonStore();
    const status = commonStore.status;
    status.bestellungPalettenErrorMsg = '';
    status.updateBestellungPalettenLoading = true;
    commonStore.setStatus(status);

    return ApiService.setListeartikelKommission(data)
      .then((res) => {
        const paletten: Listeartikel = res.data;
        commit(MutationTypes.AddOrUpdatePalette, paletten);
        dispatch(BestandActionTypes.RemoveBestandLocal, paletten.id);
      })
      .catch((error) => {
        console.log(error);
        if (error.response.status == 400) {
          status.bestellungPalettenErrorMsg = error.response.data.title;
        } else {
          status.bestellungPalettenErrorMsg = error.toString();
        }
      })
      .finally(() => {
        status.updateBestellungPalettenLoading = false;
        commonStore.setStatus(status);
      });
  },

  [ActionTypes.SetInitDate]({ commit }) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    commit(MutationTypes.SetInitDate, date);
  },

  [ActionTypes.ResetBestellungen]({ commit }) {
    commit(MutationTypes.SetBestellungen, new Array<Bestellung>());
  },

  [ActionTypes.AddOrUpdatePaletten]({ commit }, data) {
    commit(MutationTypes.AddOrUpdatePaletten, data);
  },

  [ActionTypes.AddOrUpdatePalette]({ commit }, data) {
    commit(MutationTypes.AddOrUpdatePalette, data);
  },
};

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result.split(',')[1]); // Strip out the data URL part
      } else {
        reject('Failed to convert blob to base64 string');
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
